import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import notify from '../../utils/notifications';
import {
  ArrowLeft, Edit, Trash2, CreditCard, Calendar, User, FileText,
  CheckCircle, XCircle, Clock, RefreshCw, X, Download, Printer
} from 'lucide-react';

const PagamentoDettaglio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pagamento, setPagamento] = useState(null);
  const [abbonamento, setAbbonamento] = useState(null);
  const [beneficiario, setBeneficiario] = useState(null);
  const [titolare, setTitolare] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    caricaDatiPagamento();
  }, [id]);

  const caricaDatiPagamento = async () => {
    try {
      setLoading(true);
      
      // Carica pagamento
      const pagRes = await api.get(`/pagamenti/pagamenti/${id}/`);
      setPagamento(pagRes.data);

      // Carica abbonamento
      if (pagRes.data.abbonamento) {
        const abbRes = await api.get(`/abbonamenti/abbonamenti/${pagRes.data.abbonamento}/`);
        setAbbonamento(abbRes.data);

        // Carica beneficiario
        if (abbRes.data.beneficiario) {
          const benRes = await api.get(`/anagrafiche/beneficiari/${abbRes.data.beneficiario}/`);
          setBeneficiario(benRes.data);

          // Carica titolare
          if (benRes.data.titolare) {
            const titRes = await api.get(`/anagrafiche/titolari/${benRes.data.titolare}/`);
            setTitolare(titRes.data);
          }
        }
      }

    } catch (error) {
      console.error('Errore caricamento pagamento:', error);
      notify.error('Errore nel caricamento dei dati del pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questo pagamento?')) return;
    
    const toastId = notify.loading('Eliminazione...');
    
    try {
      await api.delete(`/pagamenti/pagamenti/${id}/`);
      notify.update(toastId, { type: 'success', message: 'Pagamento eliminato con successo!' });
      setTimeout(() => navigate('/admin/pagamenti'), 500);
    } catch (error) {
      console.error('Errore eliminazione pagamento:', error);
      notify.update(toastId, { type: 'error', message: 'Errore nell\'eliminazione del pagamento' });
    }
  };

  const handleConferma = async () => {
    const toastId = notify.loading('Conferma in corso...');
    
    try {
      await api.patch(`/pagamenti/pagamenti/${id}/`, { 
        stato: 'COMPLETATO',
        data_conferma: new Date().toISOString()
      });
      notify.update(toastId, { type: 'success', message: 'Pagamento confermato!' });
      await caricaDatiPagamento();
    } catch (error) {
      console.error('Errore conferma pagamento:', error);
      notify.update(toastId, { type: 'error', message: 'Errore nella conferma' });
    }
  };

  const handleRimborso = async () => {
    if (!window.confirm('Sei sicuro di voler rimborsare questo pagamento?')) return;
    
    const toastId = notify.loading('Rimborso in corso...');
    
    try {
      await api.patch(`/pagamenti/pagamenti/${id}/`, { 
        stato: 'RIMBORSATO'
      });
      notify.update(toastId, { type: 'success', message: 'Pagamento rimborsato!' });
      await caricaDatiPagamento();
    } catch (error) {
      console.error('Errore rimborso pagamento:', error);
      notify.update(toastId, { type: 'error', message: 'Errore nel rimborso' });
    }
  };

  const getStatoBadge = (stato) => {
    switch (stato) {
      case 'COMPLETATO': return 'bg-green-100 text-green-800';
      case 'IN_ATTESA': return 'bg-yellow-100 text-yellow-800';
      case 'FALLITO': return 'bg-red-100 text-red-800';
      case 'RIMBORSATO': return 'bg-purple-100 text-purple-800';
      case 'CANCELLATO': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatoIcon = (stato) => {
    switch (stato) {
      case 'COMPLETATO': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'IN_ATTESA': return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'FALLITO': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'RIMBORSATO': return <RefreshCw className="w-5 h-5 text-purple-600" />;
      case 'CANCELLATO': return <X className="w-5 h-5 text-gray-600" />;
      default: return <CreditCard className="w-5 h-5 text-gray-600" />;
    }
  };

  const getMetodoIcon = (metodo) => {
    switch (metodo) {
      case 'CONTANTI': return '💵';
      case 'BONIFICO': return '🏦';
      case 'CARTA': return '💳';
      case 'PAYPAL': return '🅿️';
      case 'STRIPE': return '💠';
      default: return '💰';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!pagamento) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <CreditCard className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Pagamento non trovato</h2>
          <button
            onClick={() => navigate('/admin/pagamenti')}
            className="text-blue-600 hover:text-blue-700"
          >
            Torna alla lista pagamenti
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="mb-5">
          <button
            onClick={() => navigate('/admin/pagamenti')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna ai pagamenti
          </button>
          
          <div className="flex flex-wrap justify-between items-start gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Pagamento {pagamento.codice_transazione}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {abbonamento ? `Abbonamento ${abbonamento.codice}` : 'Abbonamento non disponibile'}
              </p>
            </div>
            <div className="flex gap-3">
              <span className={`px-4 py-2 text-sm rounded-full font-semibold ${getStatoBadge(pagamento.stato)}`}>
                {pagamento.stato}
              </span>
              {pagamento.stato === 'IN_ATTESA' && (
                <button
                  onClick={handleConferma}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  <CheckCircle className="w-4 h-4" />
                  Conferma
                </button>
              )}
              {pagamento.stato === 'COMPLETATO' && (
                <button
                  onClick={handleRimborso}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                >
                  <RefreshCw className="w-4 h-4" />
                  Rimborsa
                </button>
              )}
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                <Trash2 className="w-4 h-4" />
                Elimina
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Colonna sinistra */}
          <div className="lg:col-span-2 space-y-6">
            {/* Dettagli Pagamento */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-blue-600" />
                Dettagli Pagamento
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Codice Transazione</p>
                  <p className="font-semibold text-gray-900">{pagamento.codice_transazione}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Importo</p>
                  <p className="text-2xl font-bold text-green-600">€ {parseFloat(pagamento.importo).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Metodo di Pagamento</p>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{getMetodoIcon(pagamento.metodo)}</span>
                    <span className="font-semibold text-gray-900">{pagamento.metodo}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Stato</p>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatoIcon(pagamento.stato)}
                    <span className="font-semibold text-gray-900">{pagamento.stato}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Data Pagamento</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(pagamento.data_pagamento).toLocaleString('it-IT')}
                  </p>
                </div>
                {pagamento.data_conferma && (
                  <div>
                    <p className="text-sm text-gray-500">Data Conferma</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(pagamento.data_conferma).toLocaleString('it-IT')}
                    </p>
                  </div>
                )}
                {pagamento.riferimento_esterno && (
                  <div className="sm:col-span-2">
                    <p className="text-sm text-gray-500">Riferimento Esterno</p>
                    <p className="font-mono text-sm text-gray-900">{pagamento.riferimento_esterno}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Abbonamento Collegato */}
            {abbonamento && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-600" />
                  Abbonamento Collegato
                </h2>
                <div className="space-y-4">
                  <div className="border-l-4 border-purple-500 pl-4">
                    <p className="text-sm text-gray-500">Codice Abbonamento</p>
                    <p className="font-semibold text-gray-900">{abbonamento.codice}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Data Inizio</p>
                      <p className="text-sm text-gray-900">
                        {new Date(abbonamento.data_inizio).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Data Fine</p>
                      <p className="text-sm text-gray-900">
                        {new Date(abbonamento.data_fine).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Stato Abbonamento</p>
                      <span className={`inline-block px-2 py-1 text-xs rounded-full font-semibold ${
                        abbonamento.stato === 'ATTIVO' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {abbonamento.stato}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Importo Abbonamento</p>
                      <p className="text-sm text-gray-900">€ {parseFloat(abbonamento.importo).toFixed(2)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/admin/abbonamenti/${abbonamento.id}`)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                  >
                    Visualizza abbonamento completo →
                  </button>
                </div>
              </div>
            )}

            {/* Beneficiario e Titolare */}
            {beneficiario && titolare && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-4 h-4 text-green-600" />
                  Beneficiario e Titolare
                </h2>
                <div className="space-y-4">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <p className="text-sm text-gray-500">Cliente</p>
                    <p className="font-semibold text-gray-900">{beneficiario.nome} {beneficiario.cognome}</p>
                    <p className="text-sm text-gray-600">CF: {beneficiario.codice_fiscale}</p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-4">
                    <p className="text-sm text-gray-500">Tutore</p>
                    <p className="font-semibold text-gray-900">{titolare.nome} {titolare.cognome}</p>
                    <p className="text-sm text-gray-600">{titolare.email}</p>
                    <p className="text-sm text-gray-600">{titolare.telefono}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Note */}
            {pagamento.note && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-orange-600" />
                  Note
                </h2>
                <p className="text-gray-700 whitespace-pre-wrap">{pagamento.note}</p>
              </div>
            )}
          </div>

          {/* Colonna destra */}
          <div className="space-y-6">
            {/* Stats Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Riepilogo</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span>Importo Pagamento</span>
                  <span className="text-2xl font-bold text-gray-900">€ {parseFloat(pagamento.importo).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Metodo</span>
                  <span className="text-xl">{getMetodoIcon(pagamento.metodo)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Stato</span>
                  <span className="text-sm px-2 py-1 bg-white bg-opacity-20 rounded-full">
                    {pagamento.stato}
                  </span>
                </div>
              </div>
            </div>

            {/* Azioni */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Azioni</h3>
              <div className="space-y-2">
                <button
                  onClick={() => window.print()}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition"
                >
                  <Printer className="w-4 h-4" />
                  Stampa Ricevuta
                </button>
                <button
                  onClick={() => notify.info('Funzionalità in sviluppo')}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition"
                >
                  <Download className="w-4 h-4" />
                  Scarica PDF
                </button>
                <button
                  onClick={() => navigate(`/admin/clienti/${titolare?.id}`)}
                  disabled={!titolare}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition disabled:opacity-50"
                >
                  <User className="w-4 h-4" />
                  Vedi Cliente
                </button>
              </div>
            </div>

            {/* Info Sistema */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-600" />
                Info Sistema
              </h3>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-gray-500">Creato il</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(pagamento.data_creazione).toLocaleDateString('it-IT')}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Ultima modifica</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(pagamento.data_modifica).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PagamentoDettaglio;

